import type { CapitalPlanService } from "./capitalPlanService";
import type { CapitalRepository } from "./repositories/capitalRepository";
import type { DbExecutor } from "./executor";
import { invalidateHttpReads } from "./httpExecutor";

export const FUNDING_COMMANDS = ["sources","statements","prepareAutomatic","calculateAutomatic","evaluate","listPlans","savePlan","saveComparison","runs"] as const;
export type FundingCommand = typeof FUNDING_COMMANDS[number];

/** Browser uses named owner-workspace commands; scenarios never travel through arbitrary SQL. */
export function useHostedCompanyFunding(
  planner: CapitalPlanService,
  capital: CapitalRepository,
  db: Pick<DbExecutor,"whenIdle">,
  onUnauthorized?:()=>void,
  request:typeof fetch=fetch,
):void {
  const bindings=[
    [planner,"fundingSourceOptions","sources"],
    [planner,"fundingStatementOptions","statements"],
    [planner,"fundingOptimization","evaluate"],
    [planner,"prepareAutomaticFunding","prepareAutomatic"],
    [planner,"calculateAutomaticFunding","calculateAutomatic"],
    [planner,"saveFundingComparison","saveComparison"],
    [capital,"listFundingScenarios","listPlans"],
    [capital,"saveFundingScenario","savePlan"],
    [capital,"listFundingRuns","runs"],
  ] as const;
  for(const [service,method,command] of bindings)Object.defineProperty(service,method,{configurable:true,value:async(...input:unknown[])=>{
    await db.whenIdle?.();const args=[...input];while(args.length&&args[args.length-1]===undefined)args.pop();
    let response:Response;
    try{response=await request("/api/financial/funding",{method:"POST",credentials:"same-origin",headers:{"Content-Type":"application/json"},body:JSON.stringify({method:command,args})});}
    catch{throw new Error("Company planning could not reach the server. Your assumptions remain open.");}
    if(response.status===401)onUnauthorized?.();
    const payload=await response.json().catch(()=>null) as {result?:unknown;error?:string}|null;
    if(!response.ok||!payload||!("result" in payload))throw new Error(payload?.error??"The planning action was not confirmed. Review the source and retry.");
    if(command==="savePlan"||command==="saveComparison")invalidateHttpReads();
    return payload.result;
  }});
}
